import type { TFunction } from "i18next";
import { ClipboardCheck, Eye, Tag } from "lucide-react";
import {
  Fragment,
  useEffect,
  useRef,
  type CSSProperties,
  type JSX,
  type KeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";

import type { IntentName } from "@/design";
import { EmptyState } from "@/shared/components/EmptyState";
import { MarketplaceChip } from "@/shared/components/MarketplaceChip";
import type { RowAction } from "@/shared/components/RowActionMenu";
import { RowActionMenu } from "@/shared/components/RowActionMenu";
import { Button } from "@/shared/ui/button";
import { HoverCard } from "@/shared/ui/hover-card";
import { Tooltip } from "@/shared/ui/tooltip";

import { payMethod } from "../lib/detail";
import { formatBangkokDateTime, formatRelativeHours, money, shipByDiffMs } from "../lib/format";
import { MARKETPLACE_STATUS_LABEL } from "../lib/marketplace-status-labels";
import { reviewFlagIntent, topReviewReason } from "../lib/review-flags";
import { lifecycleIntent } from "../lib/status";
import type { Order } from "../lib/types";

import { ReviewFlagIcon } from "./ReviewFlagIcon";

const WARNING_WINDOW_MS = 24 * 3_600_000;
const LABEL_MISSING_STAGES = new Set(["picked", "packed"]);

/** Marketplace-status text colour by intent — no filled pill, so it reads as
 * information rather than a second chip competing with the Channel badge. */
const TEXT_INTENT: Record<IntentName, string> = {
  success: "text-[var(--intent-success-fg)]",
  warning: "text-[var(--intent-warning-fg)]",
  danger: "text-[var(--intent-danger-fg)]",
  info: "text-[var(--intent-info-fg)]",
  neutral: "text-[var(--text-secondary)]",
};

/** Marketplace-status badge intent: the internal lifecycle colour, overridden to
 * danger/warning by ship-by urgency so overdue orders still pop without a second pill. */
function marketplaceStatusIntent(order: Order): IntentName {
  if (order.shipBy) {
    const diff = shipByDiffMs(order.shipBy);
    if (diff < 0) return "danger";
    if (diff < WARNING_WINDOW_MS) return "warning";
  }
  return lifecycleIntent(order.status);
}

interface StatusTipRow {
  label: string;
  value: string;
}

/** Structured detail for the marketplace-status cell's HoverCard: fulfilment stage,
 * label, ship-by (relative + absolute in one row), courier/fleet — one row per field
 * that is actually present. Takes `t` as a param (not a hook) since it's called
 * per-row inside a `.map()`. */
function marketplaceStatusTipRows(order: Order, t: TFunction): StatusTipRow[] {
  const rows: StatusTipRow[] = [
    { label: t("tip_fulfilment_label"), value: t(`st_${order.status}`) },
  ];
  if (order.hasAwb !== undefined) {
    rows.push({
      label: t("tip_label_label"),
      value: t(order.hasAwb ? "tip_label_printed" : "tip_label_missing"),
    });
  }
  if (order.shipBy) {
    const diff = shipByDiffMs(order.shipBy);
    const { amount, unit } = formatRelativeHours(diff);
    const v = t(unit === "d" ? "d_short" : "h_short", { n: amount });
    const rel = t(diff < 0 ? "ship_overdue" : "ship_in", { v });
    rows.push({
      label: t("tip_ship_by_label"),
      value: `${rel} (${formatBangkokDateTime(order.shipBy)})`,
    });
  }
  if (order.courier) rows.push({ label: t("tip_courier_label"), value: order.courier });
  if (order.isSellerOwnFleet) rows.push({ label: t("tip_fleet_label"), value: t("tip_own_fleet") });
  return rows;
}

/** Joins the HoverCard's rows into one string for the trigger's `aria-label` —
 * keeps the accessible name informative without hovering. */
function tipRowsToText(rows: StatusTipRow[]): string {
  return rows.map((r) => `${r.label}: ${r.value}`).join(" · ");
}

/** Label/value grid rendered inside the marketplace-status HoverCard. */
function MarketplaceStatusCard({ rows }: { rows: StatusTipRow[] }): JSX.Element {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
      {rows.map((r) => (
        <Fragment key={r.label}>
          <dt className="whitespace-nowrap text-[10px] font-bold uppercase tracking-wide text-[var(--text-inverse)] opacity-60">
            {r.label}
          </dt>
          <dd className="text-[12px] font-medium">{r.value}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

interface OrdersTableProps {
  orders: Order[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleAll: () => void;
  onView: (id: string) => void;
  /** Row action for a flagged order. Optional so callers that don't wire it up (e.g. existing tests) still render. */
  onResolve?: (id: string) => void;
  /** orders:write — when false the selection column and its header checkbox are not rendered. Defaults to true for existing callers/tests. */
  selectable?: boolean;
  /** orders:write — when false the Resolve row action is not offered even for flagged orders. */
  canResolve?: boolean;
  /** True when at least one filter is active — swaps the empty-state copy to the no-match variant. */
  hasFilters?: boolean;
  /** Clears all active filters — wired to the no-match empty state's "Clear filters" action. */
  onClearFilters?: () => void;
}

const BORDER_CLASS: Record<string, string> = {
  danger: "border-l-4 border-[var(--intent-danger-fg)]",
  warning: "border-l-4 border-[var(--intent-warning-fg)]",
};

export function OrdersTable({
  orders,
  selectedIds,
  onToggleSelect,
  onToggleAll,
  onView,
  onResolve,
  selectable = true,
  canResolve = true,
  hasFilters = false,
  onClearFilters,
}: OrdersTableProps): JSX.Element {
  const { t } = useTranslation("orders");
  const headerCheckRef = useRef<HTMLInputElement>(null);

  const allSelected = orders.length > 0 && orders.every((o) => selectedIds.has(o.id));
  const someSelected = orders.some((o) => selectedIds.has(o.id));

  useEffect(() => {
    if (headerCheckRef.current) {
      headerCheckRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  if (orders.length === 0) {
    if (hasFilters) {
      return (
        <EmptyState
          title={t("empty_no_match")}
          body={t("empty_no_match_desc")}
          action={
            <Button variant="outline" size="sm" onClick={onClearFilters}>
              {t("btn_clear_filters")}
            </Button>
          }
        />
      );
    }
    return <EmptyState title={t("empty_no_orders")} body={t("empty_no_orders_desc")} />;
  }

  return (
    <div
      className="overflow-x-auto rounded-[14px] border border-[var(--premium-border)] shadow-[var(--sh-card)]"
      data-testid="orders-table"
    >
      <table className="min-w-full border-collapse">
        <thead className="sticky top-0 z-20 bg-white border-b border-[var(--row-divider)]">
          <tr>
            {selectable && (
              <th className="h-[52px] w-12 px-4">
                <input
                  ref={headerCheckRef}
                  type="checkbox"
                  checked={allSelected}
                  onChange={onToggleAll}
                  className="h-4 w-4 cursor-pointer rounded"
                  style={{ accentColor: "var(--brand-500)" }}
                  aria-label={t("cb_select_all")}
                />
              </th>
            )}
            <th className="h-[52px] px-4 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap">
              {t("th_order_date")}
            </th>
            <th className="h-[52px] px-4 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap">
              {t("th_order")}
            </th>
            <th className="h-[52px] px-4 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap">
              {t("th_customer")}
            </th>
            <th className="h-[52px] px-4 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap">
              {t("th_channel")}
            </th>
            <th className="h-[52px] px-4 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap">
              {t("th_marketplace_status")}
            </th>
            <th className="h-[52px] px-4 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap">
              {t("th_payment")}
            </th>
            <th className="h-[52px] px-4 text-right text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap">
              {t("th_total")}
            </th>
            <th className="sticky right-0 z-[6] bg-white h-[52px] px-4 shadow-[-10px_0_12px_-10px_rgba(16,24,40,0.16)]">
              <span className="sr-only">{t("th_actions")}</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--row-divider)]">
          {orders.map((order, index) => {
            const isSelected = selectable && selectedIds.has(order.id);
            const flagIntent = reviewFlagIntent(order.reviewReasons);
            const isFlagged = topReviewReason(order.reviewReasons) !== null;
            const statusIntent = marketplaceStatusIntent(order);
            const statusTipRows = marketplaceStatusTipRows(order, t);
            const statusTipText = tipRowsToText(statusTipRows);
            const actions: RowAction[] = [
              {
                key: "view",
                label: t("act_view"),
                icon: <Eye className="h-4 w-4" />,
                onSelect: () => onView(order.sale),
              },
              // Resolve only shows for a flagged order, and only once the page
              // wired up a handler (onResolve is optional so pre-existing
              // callers/tests still render without it).
              ...(isFlagged && onResolve && canResolve
                ? [
                    {
                      key: "resolve",
                      label: t("btn_resolve"),
                      icon: <ClipboardCheck className="h-4 w-4" />,
                      onSelect: () => onResolve(order.id),
                    },
                  ]
                : []),
            ];
            return (
              <tr
                key={order.id}
                data-testid="order-row"
                aria-selected={isSelected}
                tabIndex={0}
                className={[
                  "row-enter group cursor-pointer transition-colors hover:bg-[var(--row-hover)]",
                  flagIntent ? BORDER_CLASS[flagIntent] : "",
                ].join(" ")}
                onClick={() => onView(order.sale)}
                onKeyDown={(e: KeyboardEvent<HTMLTableRowElement>) => {
                  if (e.key === "Enter") onView(order.sale);
                  if (e.key === " ") {
                    e.preventDefault();
                    onView(order.sale);
                  }
                }}
                style={
                  {
                    animationDelay: `${Math.min(index, 10) * 30}ms`,
                    ...(isSelected
                      ? {
                          background: "var(--row-selected)",
                          "--row-bg": "var(--row-selected)",
                        }
                      : {}),
                  } as CSSProperties
                }
              >
                {/* Select — stop propagation so clicking checkbox doesn't navigate */}
                {selectable && (
                  <td
                    className="h-[52px] w-12 px-4"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onToggleSelect(order.id)}
                      className="h-4 w-4 cursor-pointer rounded"
                      style={{ accentColor: "var(--brand-500)" }}
                      aria-label={t("cb_select_order", { id: order.id })}
                    />
                  </td>
                )}

                {/* Order Date */}
                <td className="h-[52px] px-4 text-sm text-[var(--text-secondary)] whitespace-nowrap tabular-nums">
                  {formatBangkokDateTime(order.created)}
                </td>

                {/* Order ID (sale_order_id) + split badge */}
                <td className="h-[52px] px-4 text-sm text-[var(--text-primary)] whitespace-nowrap">
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-[12px] font-semibold">{order.sale}</span>
                    <ReviewFlagIcon reasons={order.reviewReasons} meta={order.reviewMeta} />
                    {order.splitIndex !== undefined && order.splitTotal !== undefined && (
                      <span className="inline-flex items-center rounded-full bg-[var(--surface-sunken)] px-2 py-0.5 text-[10px] font-semibold text-[var(--text-subtle)]">
                        {t("split_of", { n: order.splitIndex, m: order.splitTotal })}
                      </span>
                    )}
                  </div>
                </td>

                {/* Customer */}
                <td className="h-[52px] px-4 text-sm whitespace-nowrap">
                  <div className="text-[var(--text-primary)]">{order.cust || "—"}</div>
                  {order.phone && (
                    <div className="text-xs tabular-nums text-[var(--text-subtle)]">
                      {order.phone}
                    </div>
                  )}
                </td>

                {/* Channel: marketplace */}
                <td className="h-[52px] px-4 whitespace-nowrap">
                  <MarketplaceChip marketplace={order.mkp} />
                </td>

                {/* Marketplace status: one badge per row. Text = marketplace-reported status;
                    colour = internal fulfilment lifecycle, overridden by ship-by urgency.
                    Fulfilment stage, label, ship-by and courier detail live in the tooltip. */}
                <td
                  className="h-[52px] px-4 text-sm whitespace-nowrap"
                  data-testid="cell-marketplace-status"
                >
                  {order.marketplaceStatus ? (
                    <div className="flex items-center gap-1.5">
                      <HoverCard content={<MarketplaceStatusCard rows={statusTipRows} />}>
                        <span
                          className={`inline-flex items-center gap-1.5 font-medium ${TEXT_INTENT[statusIntent]}`}
                          data-intent={statusIntent}
                          aria-label={statusTipText}
                        >
                          <span
                            aria-hidden
                            className="h-1.5 w-1.5 shrink-0 rounded-full bg-current"
                          />
                          {MARKETPLACE_STATUS_LABEL[order.marketplaceStatus.toLowerCase()] ??
                            order.marketplaceStatus}
                        </span>
                      </HoverCard>
                      {order.hasAwb === false && LABEL_MISSING_STAGES.has(order.status) && (
                        <Tooltip content={t("tip_label_missing_icon")}>
                          <Tag
                            className="h-3.5 w-3.5 shrink-0 text-[var(--text-subtle)]"
                            aria-label={t("tip_label_missing_icon")}
                            data-testid="label-missing-icon"
                          />
                        </Tooltip>
                      )}
                    </div>
                  ) : (
                    "—"
                  )}
                </td>

                {/* Payment Method */}
                <td className="h-[52px] px-4 text-sm text-[var(--text-secondary)] whitespace-nowrap">
                  {order.payChannel || payMethod(order.pay, order.payChannel)}
                </td>

                {/* Payment Amount: blank until the settlement feed lands (mirrors Order Detail's Order Totals card) */}
                <td className="h-[52px] px-4 text-right text-sm font-mono text-[var(--text-secondary)] whitespace-nowrap tabular-nums">
                  {order.settled || order.money.total > 0
                    ? money(order.money.total, order.currency)
                    : t("awaiting_settlement")}
                </td>

                {/* Actions — sticky-right kebab; stop propagation so clicking ••• doesn't navigate */}
                <td
                  className="h-[52px] sticky right-0 z-[5] bg-[var(--row-bg,#fff)] group-hover:bg-[var(--row-hover)] shadow-[-10px_0_12px_-10px_rgba(16,24,40,0.16)] px-2 whitespace-nowrap"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <RowActionMenu actions={actions} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
